interface EsriCrsExtension {
  readonly transform?: readonly number[];
  readonly boundingVolume?: unknown;
}

interface TilesetNode {
  transform?: number[];
  boundingVolume?: unknown;
  children?: TilesetNode[];
  extensions?: {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- 3D Tiles JSON key from the ESRI CRS extension
    ESRI_crs?: EsriCrsExtension;
  };
}

interface SanitizableTilesetJson {
  extensions?: unknown;
  root?: TilesetNode;
}

/**
 * SURE/ArcGIS tilesets store a projected Z-up frame on ESRI_crs while the
 * standard 3D Tiles transform is ECEF. A local Z-up viewer that applies ECEF
 * stands the ground up. Prefer the CRS placement when present.
 */
function applyEsriCrsLocalFrame(node: TilesetNode | undefined): boolean {
  if (node == null || typeof node !== 'object') {
    return false;
  }

  let changed = false;
  const esri = node.extensions?.ESRI_crs;
  // Payloads are third-party JSON: malformed members are left untouched so
  // the renderer still receives the tileset rather than a failed request.
  const esriTransform = esri?.transform;
  if (Array.isArray(esriTransform)) {
    node.transform = [...esriTransform];
    changed = true;
  }
  if (esri?.boundingVolume != null) {
    node.boundingVolume = esri.boundingVolume;
    changed = true;
  }

  const children = Array.isArray(node.children) ? node.children : [];
  for (const child of children) {
    if (applyEsriCrsLocalFrame(child)) {
      changed = true;
    }
  }

  return changed;
}

function parseTilesetJson(text: string): SanitizableTilesetJson | undefined {
  try {
    return JSON.parse(text) as SanitizableTilesetJson;
  } catch (error: unknown) {
    // Tile payloads routed through the same fetch may not be JSON.
    if (error instanceof SyntaxError) {
      return undefined;
    }
    throw error;
  }
}

/** TilesRenderer crashes when tileset JSON has `"extensions": null`. */
export function sanitizeTilesetJsonText(text: string): string {
  const data = parseTilesetJson(text);
  if (data == null || typeof data !== 'object') {
    return text;
  }

  let changed = false;

  if (data.extensions === null) {
    data.extensions = {};
    changed = true;
  }

  if (applyEsriCrsLocalFrame(data.root)) {
    changed = true;
  }

  return changed ? JSON.stringify(data) : text;
}
